/**
 * The cockpit panel: the chat overlay's chrome.
 *
 * His instruction: make the popup look like the Millennium Falcon's dashboard. What makes that
 * cockpit recognisable is not one shape, it is BANKS OF SMALL SQUARE INDICATOR LIGHTS, rows of
 * rocker switches, and warm amber rather than the cold blue of an Imperial console. So that is
 * what this draws.
 *
 * THE LIGHTS ARE A READOUT, NOT A BLINKING FIELD, and that distinction is the whole reason this
 * file is worth having. A bank of twenty randomly lit cells is a screensaver; it would also be the
 * first thing on this board that means nothing, on a surface whose entire argument is that nothing
 * on it is invented. So the bank IS the thread: one cell per exchange, lit in that exchange's own
 * lane colour, the one in flight pulsing. Glance at it and you know how long the conversation is
 * and which lanes it has been through, which is genuinely useful when a thread survives being
 * closed and reopened.
 *
 * The cells past the end of the thread stay dark. They are frame, they are honestly empty, and
 * they are what makes the bank a fixed-width instrument rather than a list that grows.
 */

/** How many cells the bank draws. Fixed, so the panel does not resize as a conversation runs. */
const CELLS = 18;

export function FalconBank({
  turns,
}: {
  /** Lane per exchange, oldest first, plus whether that exchange is still in flight. */
  turns: { lane: 'brain' | 'zou'; pending: boolean }[];
}): JSX.Element {
  /*
   * The LAST 18, not the first. A long thread's recent history is the part worth glancing at, and
   * a bank frozen on the opening exchanges would stop tracking exactly when it started mattering.
   */
  const recent = turns.slice(-CELLS);

  return (
    <span className="mo-falcon-bank" aria-hidden="true">
      {Array.from({ length: CELLS }, (_, i) => {
        const t = recent[i];
        return (
          <span
            key={i}
            className="mo-falcon-cell"
            data-lane={t?.lane}
            data-live={t !== undefined}
            data-pending={t?.pending === true}
          />
        );
      })}
    </span>
  );
}

/**
 * A row of rocker switches, purely frame.
 *
 * Stated plainly because the bank above is not: these carry nothing, they are the panel's
 * furniture, and they are drawn rather than photographed so they take the theme and cost no image.
 * The alternating pattern is fixed, never random, so the panel looks the same every time it opens.
 * A cockpit whose switches rearrange themselves between visits is a cockpit nobody trusts.
 */
export function FalconSwitches({ count = 7 }: { count?: number }): JSX.Element {
  return (
    <span className="mo-falcon-switches" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="mo-falcon-switch" data-up={i % 3 !== 1} />
      ))}
    </span>
  );
}
